import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Icon } from 'shared-components';
import {
  ActionButton,
  ActionButtons,
  DropdownButton,
  DropdownContainer,
  DropdownItem,
  DropdownList,
  EditIcon,
  OptionName,
  OptionNameContainer,
  TrashIcon,
} from './styles';

// A custom select. Used for contacts (with edit and delete on each option) and, without
// onEdit/onDelete, as a plain select such as the security question presets.
const EditableDropdown = ({
  options,
  selectedOption = null,
  onSelect,
  onEdit = undefined,
  onDelete = undefined,
  id = undefined,
  placeholder = undefined,
  invalid = false,
  ariaDescribedBy = undefined,
  page,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const intl = useIntl();

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = option => {
    onSelect(option);
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-invalid={invalid || undefined}
        aria-describedby={ariaDescribedBy}
        $invalid={invalid}
      >
        {selectedOption
          ? selectedOption.name
          : placeholder || intl.formatMessage({ id: 'choose-contact-placeholder' })}
        <Icon size={10} type={isOpen ? 'chevron-up' : 'chevron-down'} aria-hidden="true" />
      </DropdownButton>
      {isOpen && (
        <DropdownList role="listbox">
          {options.map(option => (
            <DropdownItem
              key={option.id}
              role="option"
              aria-selected={selectedOption && selectedOption.id === option.id}
              onClick={() => handleSelect(option)}
            >
              <OptionNameContainer>
                <OptionName>{option.name}</OptionName>
              </OptionNameContainer>
              {(onEdit || onDelete) && (
                <ActionButtons>
                  {onEdit && (
                    <ActionButton
                      onClick={e => {
                        e.stopPropagation();
                        onEdit(option);
                      }}
                      aria-label={`Edit ${option.name}`}
                      type="button"
                    >
                      <EditIcon />
                    </ActionButton>
                  )}
                  {onDelete && (
                    <ActionButton
                      onClick={e => {
                        e.stopPropagation();
                        onDelete(option);
                      }}
                      aria-label={`Delete ${option.name}`}
                      type="button"
                    >
                      <TrashIcon />
                    </ActionButton>
                  )}
                </ActionButtons>
              )}
            </DropdownItem>
          ))}
        </DropdownList>
      )}
    </DropdownContainer>
  );
};

EditableDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedOption: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  }),
  onSelect: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  invalid: PropTypes.bool,
  ariaDescribedBy: PropTypes.string,
  page: PropTypes.string.isRequired,
};

export default EditableDropdown;
